// MasterTimetableView.js - COMPLETE UPDATED VERSION
// Uses the same working logic as WeeklyTimetableView

import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import LoadingSpinner from '../common/LoadingSpinner';
import { jsPDF } from 'jspdf';
import api from '../../../../api'; 
import moment from 'moment';
import { 
  buildFacultyMap, 
  resolveFacultyName, 
  aggregateFacultyForSelection,
  fetchFacultyNameById 
} from '../../hooks/useTimetable';

const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const MasterTimetableView = ({
  weekTemplate,
  subjects,
  deleteTimetable,
  isLoading,
  labBatches = [],
  onEditTimetable,
  electiveGroups,
  facultyData = [],
  baseMeta = {},
  tenantId: tenantIdProp,
  selectedSemester,
  classId,
  selectedAcademicYearId,
  isSemesterBased: isSemesterBasedProp,
  termId,
  token: propToken
}) => {
  // State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSignatoryModal, setShowSignatoryModal] = useState(false);
  const [signatories, setSignatories] = useState([]); 
  const [newSignatory, setNewSignatory] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [additionalFaculty, setAdditionalFaculty] = useState([]);
  
  const timetableRef = useRef(null);
  const fetchedFacultyRef = useRef(new Set());
  const processingRef = useRef(false);
  const lastDataHashRef = useRef(null);
  const isMountedRef = useRef(true);

  // Safe data wrappers
  const safeWeekTemplate = useMemo(() => Array.isArray(weekTemplate) ? weekTemplate : [], [weekTemplate]);
  const safeSubjects = useMemo(() => Array.isArray(subjects) ? subjects : [], [subjects]);
  const safeElectiveGroups = useMemo(() => Array.isArray(electiveGroups) ? electiveGroups : [], [electiveGroups]);
  const safeLabBatches = useMemo(() => Array.isArray(labBatches) ? labBatches : [], [labBatches]);
  const safeFacultyData = useMemo(() => Array.isArray(facultyData) ? facultyData : [], [facultyData]);

  const token = propToken || localStorage.getItem('token');
  const tenantId = tenantIdProp || localStorage.getItem('tenantId');

  // Build faculty map from all sources
  const facultyMap = useMemo(() => {
    console.log('[MasterView] Building faculty map');
    return buildFacultyMap(
      [...safeFacultyData, ...additionalFaculty], 
      safeSubjects, 
      []
    );
  }, [safeFacultyData, additionalFaculty, safeSubjects]);

  const resolveFacultyLabel = useCallback((value) => {
    return resolveFacultyName(value, facultyMap) || '';
  }, [facultyMap]);

  // Helper to get subject display name
  const getSubjectDisplayName = useCallback((subjectId) => {
    if (!subjectId) return 'Subject';
    const s = safeSubjects.find(x => String(x._id || x.id) === String(subjectId));
    return s?.name || s?.subjectName || 'Subject';
  }, [safeSubjects]);

  // Helper to get subject code
  const getSubjectCodeUpper = useCallback((subjectId) => {
    if (!subjectId) return '';
    const s = safeSubjects.find(x => String(x._id || x.id) === String(subjectId));
    const code = s?.subjectCode || s?.code || '';
    return code.toUpperCase();
  }, [safeSubjects]);

  // Helper to get batch display name
  const getBatchDisplayName = useCallback((batchId) => {
    if (!batchId) return '';
    
    let batchIdStr = '';
    if (typeof batchId === 'string') {
      batchIdStr = batchId;
    } else if (batchId._id) {
      batchIdStr = String(batchId._id);
    } else if (batchId.$oid) {
      batchIdStr = batchId.$oid;
    } else {
      batchIdStr = String(batchId);
    }
    
    const batch = safeLabBatches.find(b => {
      const bId = String(b._id || b.id || b.batchId);
      return bId === batchIdStr;
    });
    
    if (batch) {
      if (batch.batchPrefix && batch.batchNumber) {
        return `${batch.batchPrefix} ${batch.batchNumber}`;
      }
      if (batch.name) return batch.name;
      if (batch.batchName) return batch.batchName;
      if (batch.batchNumber) return `Batch ${batch.batchNumber}`;
    }
    
    if (batchId.name) return batchId.name;
    if (batchId.batchName) return batchId.batchName;
    if (batchId.batchNumber) {
      if (batchId.batchPrefix) return `${batchId.batchPrefix} ${batchId.batchNumber}`;
      return `Batch ${batchId.batchNumber}`;
    }
    
    return '';
  }, [safeLabBatches]);

  // Helper to enrich elective selections with subgroups from group definition
  const enrichElectiveSelectionsWithSubgroups = useCallback((entry) => {
    if (!entry.electiveGroupId || !entry.electiveSelections || entry.electiveSelections.length === 0) {
      return entry;
    }

    const group = safeElectiveGroups.find(g => String(g._id) === String(entry.electiveGroupId));
    if (!group || !group.subjects) {
      return entry;
    }

    const enrichedSelections = entry.electiveSelections.map(selection => {
      const groupSubject = group.subjects.find(s => 
        String(s.subjectId) === String(selection.subjectId)
      );

      if (groupSubject?.subGroups && groupSubject.subGroups.length > 0 && 
          (!selection.subgroupSelections || selection.subgroupSelections.length === 0)) {
        
        const subgroupSelections = groupSubject.subGroups.map(subgroup => ({
          subGroupId: subgroup.subGroupId,
          facultyId: subgroup.facultyId,
          room: subgroup.room || selection.room || entry.room,
          facultyName: ''
        }));

        return {
          ...selection,
          subgroupSelections: subgroupSelections
        };
      }
      
      return selection;
    });

    return {
      ...entry,
      electiveSelections: enrichedSelections
    };
  }, [safeElectiveGroups]);

  // Helper to get session type
  const getSessionType = useCallback((entry) => {
    if (entry.type === 'Lab' || (entry.subjectId && entry.batchId) || entry.isLab) {
      return 'lab';
    }
    if (entry.type === 'Break') {
      return 'break';
    }
    if (entry.type === 'Custom') {
      return 'custom';
    }
    if (entry.electiveGroupId) {
      const electiveGroup = safeElectiveGroups.find(g => String(g._id) === String(entry.electiveGroupId));
      const groupType = electiveGroup?.groupType || electiveGroup?.groupCategory || electiveGroup?.category || '';
      if (groupType === 'language' || groupType === 'Language') {
        return 'language';
      }
      return 'elective';
    }
    return 'regular';
  }, [safeElectiveGroups]);

  const getGroupDisplayName = useCallback((groupId) => {
    const group = safeElectiveGroups.find(g => String(g._id) === String(groupId));
    if (!group) return 'Group';
    
    const groupType = group.groupType || group.groupCategory || group.category || '';
    const isLanguage = groupType === 'language' || groupType === 'Language';
    
    if (isLanguage) {
      return group.groupName || group.name || 'Language';
    }
    return `Elective: ${group.groupName || group.name || 'Elective'}`;
  }, [safeElectiveGroups]);

  const getElectiveGroupLabel = useCallback((groupId) => {
    const g = safeElectiveGroups.find(x => String(x._id) === String(groupId));
    return g?.label || '';
  }, [safeElectiveGroups]);

  const getElectiveGroupName = useCallback((groupId) => {
    const g = safeElectiveGroups.find(x => String(x._id) === String(groupId));
    return g?.groupName || 'Elective Group';
  }, [safeElectiveGroups]);

  // Process missing faculty
  const processMissingFaculty = useCallback(async (entries) => {
    if (processingRef.current) return;
    if (!entries || !isMountedRef.current) return;

    const dataHash = JSON.stringify({
      entries: entries.map(entry => ({
        facultyUserId: entry.facultyUserId,
        facultyId: entry.facultyId,
        electiveSelections: entry.electiveSelections?.map(s => ({
          facultyId: s.facultyId,
          subgroupSelections: s.subgroupSelections?.map(sg => sg.facultyId)
        }))
      }))
    });

    if (lastDataHashRef.current === dataHash) return;
    
    processingRef.current = true;
    
    try {
      const facultyIds = new Set();
      
      entries.forEach(entry => {
        if (entry.facultyUserId) facultyIds.add(String(entry.facultyUserId));
        if (entry.facultyId) facultyIds.add(String(entry.facultyId));
        
        (entry.electiveSelections || []).forEach(selection => {
          if (selection.facultyId) facultyIds.add(String(selection.facultyId));
          
          (selection.subgroupSelections || []).forEach(subgroup => {
            if (subgroup.facultyId) facultyIds.add(String(subgroup.facultyId));
          });
        });
      });

      const missingIds = Array.from(facultyIds).filter(id => 
        !facultyMap.has(id) && id.trim() && !fetchedFacultyRef.current.has(id)
      );

      if (missingIds.length === 0) {
        lastDataHashRef.current = dataHash;
        return;
      }

      console.log('[MasterView] Fetching missing faculty:', missingIds);

      const facultyPromises = missingIds.map(async (facultyId) => {
        try {
          const name = await fetchFacultyNameById(facultyId, token);
          fetchedFacultyRef.current.add(facultyId);
          return { id: facultyId, name };
        } catch {
          fetchedFacultyRef.current.add(facultyId);
          return null;
        }
      });
      
      const facultyResults = await Promise.all(facultyPromises);
      const validResults = facultyResults.filter(Boolean);
      
      if (validResults.length > 0 && isMountedRef.current) {
        setAdditionalFaculty(prev => {
          const newFaculty = validResults.filter(r => 
            !prev.some(p => p.userId === r.id)
          );
          return [...prev, ...newFaculty.map(r => ({ 
            userId: r.id, 
            firstName: r.name,
            name: r.name 
          }))];
        });
      }
      
      lastDataHashRef.current = dataHash;
    } finally {
      processingRef.current = false;
    }
  }, [facultyMap, token]);

  // Enriched valid entries with subgroup data and faculty resolution
  const [validEntries, setValidEntries] = useState([]);

  useEffect(() => {
    let isActive = true;
    const enrichEntries = async () => {
      const filtered = safeWeekTemplate.filter(e => e && e.timeSlot && e.timeSlot.start && e.timeSlot.end);
      const enriched = await Promise.all(filtered.map(async (entry) => {
        // First enrich with subgroups from group definition
        const entryWithSubgroups = enrichElectiveSelectionsWithSubgroups(entry);
        // Resolve faculty name for regular subjects
        const facultyVal = entryWithSubgroups.facultyUserId || entryWithSubgroups.facultyId || entryWithSubgroups.faculty || entryWithSubgroups.facultyName;
        let resolvedName = resolveFacultyLabel(facultyVal);
        const sessionType = getSessionType(entryWithSubgroups);
        let labFacultyName = '';
        // For lab sessions with batch, fetch faculty from backend and resolve name
        if (sessionType === 'lab' && entryWithSubgroups.batchId && entryWithSubgroups.subjectId) {
          try {
            const res = await api.get(`/api/classManager/lab-batches/${entryWithSubgroups.batchId}/subjects/${entryWithSubgroups.subjectId}/faculty`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            if (res.data.faculty && res.data.faculty.length > 0) {
              const names = await Promise.all(res.data.faculty.map(f => fetchFacultyNameById(f.facultyId, token)));
              labFacultyName = names.filter(Boolean).join(', ');
            } else {
              labFacultyName = 'No Faculty Assigned';
            }
          } catch (err) {
            labFacultyName = 'Error fetching faculty';
          }
        }
        // Enrich elective selections with faculty names
        const enrichedElectives = (entryWithSubgroups.electiveSelections || []).map(selection => ({
          ...selection,
          facultyName: aggregateFacultyForSelection(selection, facultyMap) || selection.facultyName || '',
          subgroupSelections: (selection.subgroupSelections || []).map(sg => ({
            ...sg,
            facultyName: resolveFacultyLabel(sg.facultyId) || ''
          }))
        }));
        const isElective = sessionType === 'elective' || sessionType === 'language';
        const isLanguage = sessionType === 'language';
        const isLab = sessionType === 'lab';
        const isBreak = sessionType === 'break';
        const isCustom = sessionType === 'custom';
        // Resolve batch name
        let batchName = '';
        if (entryWithSubgroups.batchId) {
          batchName = getBatchDisplayName(entryWithSubgroups.batchId);
        }
        return {
          ...entryWithSubgroups,
          facultyName: labFacultyName || resolvedName || entryWithSubgroups.facultyName || '',
          electiveSelections: enrichedElectives,
          isElective,
          isLanguage,
          isLab,
          isBreak,
          isCustom,
          sessionType,
          electiveGroupName: entryWithSubgroups.electiveGroupId ? getElectiveGroupName(entryWithSubgroups.electiveGroupId) : null,
          electiveGroupLabel: entryWithSubgroups.electiveGroupId ? getElectiveGroupLabel(entryWithSubgroups.electiveGroupId) : null,
          electiveGroupDisplay: entryWithSubgroups.electiveGroupId ? getGroupDisplayName(entryWithSubgroups.electiveGroupId) : null,
          subjectName: entryWithSubgroups.subjectName || getSubjectDisplayName(entryWithSubgroups.subjectId),
          subjectCode: entryWithSubgroups.subjectCode || getSubjectCodeUpper(entryWithSubgroups.subjectId),
          batchName: batchName || entryWithSubgroups.batchName
        };
      }));
      // Process missing faculty
      processMissingFaculty(enriched);
      if (isActive) setValidEntries(enriched);
    };
    enrichEntries();
    return () => { isActive = false; };
  }, [safeWeekTemplate, enrichElectiveSelectionsWithSubgroups, resolveFacultyLabel, facultyMap, getSessionType, getBatchDisplayName, getElectiveGroupName, getElectiveGroupLabel, getGroupDisplayName, getSubjectDisplayName, getSubjectCodeUpper, processMissingFaculty, token]);

  // Time Helpers
  const toMinutes = (timeStr) => {
    if (!timeStr) return 0;
    const [h, m] = timeStr.split(':').map(Number);
    return h * 60 + m;
  };

  const formatTime12 = (timeStr) => {
    if (!timeStr) return '';
    const m = moment(timeStr, 'HH:mm');
    return m.isValid() ? m.format('hh:mm A') : timeStr;
  };

  // Grid Slots
  const gridSlots = useMemo(() => {
    const timePoints = new Set();
    validEntries.forEach(entry => {
      timePoints.add(entry.timeSlot.start);
      timePoints.add(entry.timeSlot.end);
    });
    const sortedPoints = Array.from(timePoints).sort((a, b) => toMinutes(a) - toMinutes(b));
    const slots = [];
    for (let i = 0; i < sortedPoints.length - 1; i++) {
      slots.push({
        start: sortedPoints[i],
        end: sortedPoints[i+1],
        label: `${formatTime12(sortedPoints[i])} - ${formatTime12(sortedPoints[i+1])}`
      });
    }
    return slots;
  }, [validEntries]);

  // Academic Info
  const academicInfo = useMemo(() => {
    const get = (obj, path) => path.split('.').reduce((o, k) => (o && o[k] !== undefined ? o[k] : undefined), obj);
    const findFirst = (paths) => {
       for(const e of safeWeekTemplate) {
          if(!e) continue;
          for(const p of paths) { const v = get(e,p); if(v) return v; }
       }
       return '';
    };

    const academicYear = baseMeta.academicYearName || baseMeta.academicYear || findFirst(['academicYear', 'academicYearName', 'session.year']);
    
    const isSemesterBasedFinal = baseMeta.isSemesterBased !== undefined ? baseMeta.isSemesterBased : false;
    
    let semesterOrTerm = '';
    let displayLabel = '';
    
    if (isSemesterBasedFinal) {
      semesterOrTerm = baseMeta.semester || baseMeta.semesterName || baseMeta.term || findFirst(['semester', 'sem', 'semesterName']);
      if (semesterOrTerm) displayLabel = semesterOrTerm;
    } else {
      semesterOrTerm = baseMeta.term || baseMeta.termName || baseMeta.semester || findFirst(['term', 'termName', 'termLabel']);
      if (semesterOrTerm) displayLabel = semesterOrTerm;
    }
    
    if (!semesterOrTerm && selectedSemester) {
      semesterOrTerm = selectedSemester;
      displayLabel = selectedSemester;
    }
    
    const className = baseMeta.className || baseMeta.class || baseMeta.classTitle || findFirst(['className', 'class.name', 'gradeName']);
    const departmentName = baseMeta.departmentName || baseMeta.deptName || baseMeta.department || findFirst(['departmentName', 'deptName']);
    const section = baseMeta.sectionName || baseMeta.section || findFirst(['sectionName', 'section']);
    const termNumber = baseMeta.termNumber || findFirst(['termNumber']);
    
    return { 
      academicYear, 
      semester: semesterOrTerm, 
      displayLabel,
      className, 
      departmentName, 
      section, 
      isSemesterBased: isSemesterBasedFinal,
      termNumber
    };
  }, [baseMeta, safeWeekTemplate, selectedSemester]);

  // Handlers
  const handleDeleteTimetable = async () => {
    setShowDeleteModal(false);
    if (typeof deleteTimetable === 'function') await deleteTimetable();
  };

  const handleViewDetails = (entry) => setSelectedSession(entry);

  const handleAddSignatory = (val) => {
    const toAdd = val || newSignatory;
    if (toAdd && toAdd.trim()) {
      setSignatories([...signatories, toAdd.trim()]);
      if(!val) setNewSignatory('');
    }
  };

  const handleRemoveSignatory = (i) => {
    const n = [...signatories]; n.splice(i, 1); setSignatories(n);
  };

  // PDF Generation
  const generatePDF = async (finalSignatories) => {
    if (!timetableRef.current) return;
    setIsDownloading(true);

    try {
      const org = await fetchOrganizationInfo(tenantId, token);
      const logoData = org?.logoUrl ? await fetchImage(org.logoUrl, token) : null;

      const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const w = pdf.internal.pageSize.getWidth();
      const h = pdf.internal.pageSize.getHeight();
      const margin = 10;
      let y = margin;

      const drawHeader = () => {
         let cy = margin;
         const logoSize = 15;
         if (logoData) {
            try { pdf.addImage(logoData, 'PNG', margin, cy, logoSize, logoSize); } catch(e){ try{pdf.addImage(logoData,'JPEG',margin,cy,logoSize,logoSize);}catch(_){} }
         }
         
         pdf.setFont('helvetica', 'bold');
         pdf.setFontSize(14);
         pdf.setTextColor(0, 51, 102);
         const orgName = org?.name || 'ACADEMIC INSTITUTION';
         pdf.text(orgName, w/2, cy + 5, { align: 'center' });

         if (org?.address) {
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(8);
            pdf.setTextColor(80);
            const addr = org.address.replace(/\n/g, ', ');
            pdf.text(addr, w/2, cy + 10, { align: 'center' });
            cy += 14;
         } else {
            cy += 10;
         }

         cy = Math.max(cy, margin + logoSize + 3);
         
         const { academicYear, semester, className, departmentName, section, isSemesterBased } = academicInfo;
         const parts = [];
         
         if (academicYear) parts.push(`Academic Year - ${academicYear}`);
         if (semester) {
           if (isSemesterBased) {
             parts.push(`Semester - ${semester}`);
           } else {
             parts.push(`Term - ${semester}`);
           }
         }
         if (className) {
           let classDisplay = className;
           if (section) {
             classDisplay = `${className} - Section ${section}`;
           }
           parts.push(`Class - ${classDisplay}`);
         } else if (departmentName) {
           parts.push(`Department - ${departmentName}`);
         }

         pdf.setDrawColor(200);
         pdf.line(margin, cy, w-margin, cy);
         cy += 5;

         if (parts.length > 0) {
           pdf.setFont('helvetica', 'bold');
           pdf.setFontSize(10);
           pdf.setTextColor(0);
           pdf.text(parts.join(' | '), w/2, cy, { align: 'center' });
         }
         return cy + 6;
      };

      y = drawHeader();

      const headerH = 8;
      const footerReserve = finalSignatories.length > 0 ? 45 : 15;
      const availableGridHeight = h - y - footerReserve;
      const numDays = daysOfWeek.length;
      const rowH = availableGridHeight / (numDays + 1);

      const dayColW = 20;
      const cols = gridSlots.length ? gridSlots : [{ label: 'Empty' }];
      const tableW = w - (margin * 2);
      const slotW = (tableW - dayColW) / cols.length;

      pdf.setFillColor(41, 128, 185); 
      pdf.rect(margin, y, tableW, headerH, 'F');
      pdf.setTextColor(255);
      pdf.setFontSize(7);
      pdf.setFont('helvetica', 'bold');
      pdf.text('DAY', margin + dayColW/2, y + 5, { align: 'center' });
      cols.forEach((c, i) => {
         pdf.text(c.label, margin + dayColW + (i*slotW) + slotW/2, y + 5, { align: 'center' });
      });
      y += headerH;

      daysOfWeek.forEach((day, di) => {
         if(di%2===0) pdf.setFillColor(245, 247, 250); else pdf.setFillColor(255,255,255);
         pdf.rect(margin, y, tableW, rowH, 'F');
         pdf.setDrawColor(220);
         pdf.rect(margin, y, tableW, rowH);

         pdf.setTextColor(0);
         pdf.setFont('helvetica', 'bold');
         pdf.setFontSize(8);
         pdf.text(day.substring(0,3).toUpperCase(), margin + dayColW/2, y + rowH/2 + 1, { align: 'center' });
         pdf.line(margin + dayColW, y, margin + dayColW, y + rowH);

         let skip = 0;
         cols.forEach((slot, si) => {
            if(skip > 0) { skip--; return; }
            const cx = margin + dayColW + (si*slotW);
            
            const entries = validEntries.filter(e => e.dayOfWeek === day && e.timeSlot.start === slot.start);

            if(entries.length > 0) {
               let maxSpan = 1;
               entries.forEach(e => {
                   const endIndex = gridSlots.findIndex(s => s.end === e.timeSlot.end);
                   if(endIndex !== -1 && endIndex >= si) {
                       const span = (endIndex - si) + 1;
                       if(span > maxSpan) maxSpan = span;
                   }
               });
               skip = maxSpan - 1;
               const cw = maxSpan * slotW;

               const primary = entries[0];
               if(primary.type === 'Break') pdf.setFillColor(255, 250, 240);
               else if(primary.type === 'Lab') pdf.setFillColor(243, 235, 255);
               else if(primary.type === 'Custom') pdf.setFillColor(255, 248, 240);
               else pdf.setFillColor(255, 255, 255);

               pdf.rect(cx, y, cw, rowH, 'F');
               pdf.rect(cx, y, cw, rowH);

               const entryHeight = rowH / entries.length;
               
               entries.forEach((entry, idx) => {
                   const localY = y + (idx * entryHeight);
                   const sessionType = getSessionType(entry);
                   const isBreak = sessionType === 'break';
                   const isCustom = sessionType === 'custom';

                   pdf.setTextColor(0);

                   if (isBreak || isCustom) {
                       pdf.setFontSize(7);
                       pdf.setFont('helvetica', 'bold');
                       const label = entry.label || (isBreak ? 'BREAK' : 'CUSTOM');
                       pdf.text(label, cx+cw/2, localY+(entryHeight/2)+1, {align:'center'});
                   } else if (sessionType === 'lab') {
                       pdf.setFontSize(7);
                       pdf.setFont('helvetica', 'bold');
                       const code = getSubjectCodeUpper(entry.subjectId) || 'LAB';
                       const batchName = getBatchDisplayName(entry.batchId);
                       pdf.text(code, cx+cw/2, localY+(entryHeight/3), {align:'center'});
                       if (batchName) {
                         pdf.setFontSize(5);
                         pdf.text(batchName, cx+cw/2, localY+(entryHeight*2/3), {align:'center'});
                       }
                   } else {
                       let code = '';
                       if (sessionType === 'language') {
                         code = 'LANG';
                       } else if (sessionType === 'elective') {
                         code = 'ELECTIVE';
                       } else {
                         code = getSubjectCodeUpper(entry.subjectId) || 'SUB';
                       }
                       
                       pdf.setFontSize(8);
                       pdf.setFont('helvetica', 'bold');
                       pdf.setTextColor(50);
                       pdf.text(code, cx+cw/2, localY+(entryHeight/2), {align:'center'});

                       if(entry.room) {
                           pdf.setFontSize(6);
                           pdf.setTextColor(100);
                           pdf.text(`R:${entry.room}`, cx+cw-1, localY+entryHeight-1, {align:'right'});
                       }
                   }
                   
                   if (idx < entries.length - 1) {
                       pdf.setDrawColor(200);
                       pdf.line(cx, localY+entryHeight, cx+cw, localY+entryHeight);
                   }
               });

            } else {
               pdf.line(cx+slotW, y, cx+slotW, y+rowH);
            }
         });
         y += rowH;
      });

      if (finalSignatories && finalSignatories.length > 0) {
          const sigY = h - margin - 25;
          const sigW = (w - (margin * 2)) / finalSignatories.length;
          
          finalSignatories.forEach((sig, i) => {
              const sx = margin + (i * sigW);
              pdf.setDrawColor(0); pdf.setLineWidth(0.5);
              pdf.line(sx + 10, sigY, sx + sigW - 10, sigY);
              
              pdf.setFont('helvetica', 'bold'); 
              pdf.setFontSize(9); 
              pdf.setTextColor(0);
              pdf.text(sig, sx + sigW/2, sigY + 5, { align: 'center' });
          });
      }

      // Page 2: Subject mapping
      pdf.addPage();
      let mapY = drawHeader();
      
      pdf.setFont('helvetica', 'bold'); pdf.setFontSize(11); pdf.setTextColor(0, 51, 102);
      pdf.text('SUBJECT & FACULTY DETAILS', w/2, mapY + 5, { align: 'center' });
      mapY += 10;

      const mappingRows = [];
      const seenKeys = new Set();

      validEntries.forEach(entry => {
          const sessionType = getSessionType(entry);
          if (sessionType === 'break' || sessionType === 'custom') return;

          const addRow = (code, subject, faculty) => {
             const key = `${code}|${subject}|${faculty}`;
             if (!seenKeys.has(key)) {
                seenKeys.add(key);
                mappingRows.push({ code, subject, faculty });
             }
          };

          if (sessionType === 'language' || sessionType === 'elective') {
            const selections = entry.electiveSelections || [];
            const groupDisplay = getGroupDisplayName(entry.electiveGroupId);
            
            selections.forEach(sel => {
              const code = getSubjectCodeUpper(sel.subjectId);
              const subjectName = getSubjectDisplayName(sel.subjectId);
              
              if (sel.subgroupSelections && sel.subgroupSelections.length > 0) {
                sel.subgroupSelections.forEach((subgroup, sgIdx) => {
                  const subgroupDisplay = `${groupDisplay}: ${subjectName} (Group ${sgIdx + 1})`;
                  const faculty = subgroup.facultyName || 'Not Assigned';
                  addRow(code || (sessionType === 'language' ? 'LANG' : 'ELEC'), subgroupDisplay, faculty);
                });
              } else {
                const displaySubject = `${groupDisplay}: ${subjectName}`;
                let faculty = sel.facultyName || 'Not Assigned';
                addRow(code || (sessionType === 'language' ? 'LANG' : 'ELEC'), displaySubject, faculty);
              }
            });
          } else if (sessionType === 'lab') {
            const code = getSubjectCodeUpper(entry.subjectId) || 'LAB';
            const name = getSubjectDisplayName(entry.subjectId);
            const f = entry.facultyName || 'Not Assigned';
            addRow(code, name, f);
          } else {
            const code = getSubjectCodeUpper(entry.subjectId);
            const name = getSubjectDisplayName(entry.subjectId);
            const f = entry.facultyName || 'Not Assigned';
            addRow(code, name, f);
          }
      });
      
      mappingRows.sort((a,b) => (a.code || '').localeCompare(b.code || ''));

      const mCol1 = 15; const mCol2 = 35; const mCol3 = 100;
      const mCol4 = w - margin*2 - mCol1 - mCol2 - mCol3;

      const drawMappingHeader = (cy) => {
          pdf.setFillColor(230); pdf.rect(margin, cy, w-margin*2, 8, 'F');
          pdf.setTextColor(0); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9);
          pdf.text('Sl No', margin + 2, cy + 5);
          pdf.text('Subject Code', margin + mCol1 + 2, cy + 5);
          pdf.text('Subject Name', margin + mCol1 + mCol2 + 2, cy + 5);
          pdf.text('Faculty Name', margin + mCol1 + mCol2 + mCol3 + 2, cy + 5);
          return cy + 8;
      };
      mapY = drawMappingHeader(mapY);
      pdf.setFont('helvetica', 'normal');

      mappingRows.forEach((row, idx) => {
          if (mapY + 10 > h - margin - 10) { 
              pdf.addPage(); mapY = margin + 10; mapY = drawMappingHeader(mapY);
          }
          const h1 = pdf.splitTextToSize(row.code || '-', mCol2 - 4).length * 4;
          const h2 = pdf.splitTextToSize(row.subject || '-', mCol3 - 4).length * 4;
          const h3 = pdf.splitTextToSize(row.faculty || '-', mCol4 - 4).length * 4;
          const cellH = Math.max(7, h1, h2, h3) + 2;

          if (idx % 2 === 0) { pdf.setFillColor(250); pdf.rect(margin, mapY, w-margin*2, cellH, 'F'); }
          pdf.setDrawColor(220); pdf.rect(margin, mapY, w-margin*2, cellH);

          pdf.text(String(idx + 1), margin + 2, mapY + 5);
          pdf.text(String(row.code || '-'), margin + mCol1 + 2, mapY + 5, { maxWidth: mCol2 - 4 });
          pdf.text(String(row.subject || '-'), margin + mCol1 + mCol2 + 2, mapY + 5, { maxWidth: mCol3 - 4 });
          pdf.text(String(row.faculty || '-'), margin + mCol1 + mCol2 + mCol3 + 2, mapY + 5, { maxWidth: mCol4 - 4 });

          pdf.line(margin+mCol1, mapY, margin+mCol1, mapY+cellH);
          pdf.line(margin+mCol1+mCol2, mapY, margin+mCol1+mCol2, mapY+cellH);
          pdf.line(margin+mCol1+mCol2+mCol3, mapY, margin+mCol1+mCol2+mCol3, mapY+cellH);

          mapY += cellH;
      });

      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(150);
        pdf.text(`Page ${i} of ${totalPages}`, w - margin, h - 5, { align: 'right' });
      }

      const sanitize = (s) => String(s || '').trim().replace(/[^a-zA-Z0-9-]/g, '');
      const { academicYear, semester, className, isSemesterBased } = academicInfo;
      const fNameParts = [
          'TT',
          sanitize(className),
          isSemesterBased ? `Sem${sanitize(semester)}` : `Term${sanitize(semester)}`,
          sanitize(academicYear)
      ].filter(Boolean);
      
      pdf.save(`${fNameParts.join('_')}.pdf`);

    } catch (err) { 
      console.error(err); 
      alert("Failed to generate PDF"); 
    } finally { 
      setIsDownloading(false); 
    }
  };

  const fetchOrganizationInfo = async (tenantId, token) => {
    try {
      const response = await api.get(`/api/organization/basic-info/${tenantId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch organization info:', error);
      return null;
    }
  };

  const fetchImage = async (url, token) => {
    if(!url) return null;
    try {
      const r = await fetch(url, { 
        mode:'cors', 
        headers: token ? { Authorization:`Bearer ${token}` } : undefined 
      });
      if(!r.ok) return null;
      const b = await r.blob();
      return new Promise(res => { 
        const fr = new FileReader(); 
        fr.onloadend=()=>res(fr.result); 
        fr.readAsDataURL(b); 
      });
    } catch(e) { 
      return null; 
    }
  };

  // Cleanup
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  if (isLoading) {
    return <div className="flex justify-center py-12"><LoadingSpinner size="large" /></div>;
  }

  const { academicYear, semester, className: headerClassName, departmentName, section, isSemesterBased } = academicInfo;

  return (
    <div className="space-y-6 font-sans text-gray-800">
      {/* HEADER */}
      <div className="bg-white border-l-4 border-blue-600 rounded-lg shadow-sm p-6 flex flex-col md:flex-row justify-between items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Academic Timetable</h1>
          <div className="flex flex-wrap gap-3 mt-2 text-sm text-gray-600">
            {academicYear && (
              <span className="px-2 py-0.5 bg-gray-100 rounded border">
                Academic Year - {academicYear}
              </span>
            )}
            {semester && (
              <span className="px-2 py-0.5 bg-gray-100 rounded border">
                {isSemesterBased ? `Semester - ${semester}` : `Term - ${semester}`}
              </span>
            )}
            {headerClassName && (
              <span className="px-2 py-0.5 bg-gray-100 rounded border">
                Class - {headerClassName}{section ? ` - Section ${section}` : ''}
              </span>
            )}
            {departmentName && !headerClassName && (
              <span className="px-2 py-0.5 bg-gray-100 rounded border">
                Department - {departmentName}
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-2">
           {typeof onEditTimetable === 'function' && (
            <button onClick={onEditTimetable} className="px-4 py-2 bg-white border border-gray-300 rounded hover:bg-gray-50 font-medium text-sm transition-colors">
              ✏️ Edit
            </button>
          )}
          <button onClick={() => setShowSignatoryModal(true)} disabled={isDownloading} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium text-sm shadow-sm transition-colors">
            {isDownloading ? 'Generating...' : '⬇ PDF'}
          </button>
          <button onClick={() => setShowDeleteModal(true)} className="px-4 py-2 bg-white border border-red-200 text-red-600 rounded hover:bg-red-50 font-medium text-sm">
            🗑️
          </button>
        </div>
      </div>

      {/* TIMETABLE GRID */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-300 overflow-hidden" ref={timetableRef}>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-800 text-white text-xs uppercase tracking-wider">
                <th className="px-4 py-3 border-r border-gray-600 w-24 text-left font-bold min-w-[100px]">Day</th>
                {gridSlots.map((slot, i) => (
                  <th key={i} className="px-2 py-3 border-r border-gray-600 last:border-r-0 text-center font-bold min-w-[140px]">
                    {slot.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="text-sm">
              {daysOfWeek.map((day, dIndex) => {
                let skipCount = 0;
                return (
                  <tr key={day} className={dIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-4 py-4 font-bold text-gray-700 border-r border-gray-300 border-t border-gray-200">
                      {day}
                    </td>
                    {gridSlots.map((slot, sIndex) => {
                      if (skipCount > 0) { skipCount--; return null; }
                      
                      const entries = validEntries.filter(e => e.dayOfWeek === day && e.timeSlot.start === slot.start);

                      if (entries.length > 0) {
                        let maxSpan = 1;
                        entries.forEach(e => {
                           const endIndex = gridSlots.findIndex(s => s.end === e.timeSlot.end);
                           if(endIndex !== -1 && endIndex >= sIndex) {
                               const span = (endIndex - sIndex) + 1;
                               if(span > maxSpan) maxSpan = span;
                           }
                        });
                        skipCount = maxSpan - 1;

                        const primary = entries[0];
                        const sessionType = getSessionType(primary);
                        const isBreak = sessionType === 'break';
                        const isCustom = sessionType === 'custom';
                        
                        let cardClass = "bg-white border-gray-200";
                        if (isBreak) cardClass = "bg-amber-50 border-amber-200 text-amber-800";
                        else if (isCustom) cardClass = "bg-orange-50 border-orange-200 text-orange-800";
                        else if (sessionType === 'lab') cardClass = "bg-purple-50 border-purple-200";
                        else if (sessionType === 'language') cardClass = "bg-green-50 border-green-200";
                        else if (sessionType === 'elective') cardClass = "bg-indigo-50 border-indigo-200";
                        else cardClass = "bg-blue-50 border-blue-200";

                        return (
                          <td key={sIndex} colSpan={maxSpan} className="p-1 border-r border-t border-gray-300 last:border-r-0 h-auto min-h-[100px] align-top">
                             <div className="flex flex-col gap-1 h-full">
                                {entries.map((entry, idx) => {
                                   const entrySessionType = getSessionType(entry);
                                   const isEntryBreak = entrySessionType === 'break';
                                   const isEntryCustom = entrySessionType === 'custom';
                                   
                                   return (
                                     <div 
                                       key={idx}
                                       className={`flex-1 rounded border p-2 flex flex-col justify-between transition-all hover:shadow-md cursor-pointer ${cardClass} min-h-[90px]`}
                                       onClick={() => !isEntryBreak && handleViewDetails(entry)}
                                     >
                                         <div className="flex justify-between items-start">
                                            {isEntryBreak || isEntryCustom ? (
                                                <div className="w-full text-center font-bold opacity-80 text-xs tracking-wider uppercase flex items-center justify-center h-full pt-4">
                                                  {entry.label || (isEntryBreak ? 'BREAK' : 'SESSION')}
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="font-bold text-xs uppercase tracking-tight">
                                                        {entrySessionType === 'language' && (
                                                            <span className="bg-green-100 text-green-800 px-1.5 py-0.5 rounded text-[10px] font-bold">LANGUAGE</span>
                                                        )}
                                                        {entrySessionType === 'elective' && (
                                                            <span className="bg-indigo-100 text-indigo-800 px-1.5 py-0.5 rounded text-[10px] font-bold">ELECTIVE</span>
                                                        )}
                                                        {entrySessionType === 'lab' && (
                                                            <span className="bg-purple-100 text-purple-800 px-1.5 py-0.5 rounded text-[10px] font-bold">LAB</span>
                                                        )}
                                                        {entrySessionType === 'regular' && (
                                                            <span className="bg-gray-700 text-white px-1.5 py-0.5 rounded text-[10px]">
                                                                {getSubjectCodeUpper(entry.subjectId) || 'SUB'}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="text-[10px] font-mono opacity-60 font-semibold whitespace-nowrap ml-1">
                                                        {entry.room && <span>R:{entry.room}</span>}
                                                    </div>
                                                </>
                                            )}
                                        </div>

                                        {!isEntryBreak && !isEntryCustom && (
                                            <>
                                            <div className="text-xs font-bold text-gray-800 leading-snug my-1 line-clamp-2">
                                                {entrySessionType === 'language' || entrySessionType === 'elective' 
                                                    ? getGroupDisplayName(entry.electiveGroupId)
                                                    : (entry.subjectName || getSubjectDisplayName(entry.subjectId))}
                                            </div>
                                            <div className="text-[11px] text-gray-600 border-t border-current/10 pt-1 mt-1">
                                                {entrySessionType === 'language' || entrySessionType === 'elective' 
                                                    ? (() => {
                                                        const hasAnySubgroups = entry.electiveSelections?.some(sel => 
                                                          sel.subgroupSelections && sel.subgroupSelections.length > 0
                                                        );
                                                        
                                                        if (hasAnySubgroups) {
                                                          return (
                                                            <div className="space-y-1">
                                                              <span className="text-purple-600 font-semibold">Groups:</span>
                                                              {entry.electiveSelections?.map((sel, selIdx) => (
                                                                sel.subgroupSelections?.map((sg, sgIdx) => {
                                                                  return (
                                                                    <div key={`${selIdx}-${sgIdx}`} className="text-[10px] text-gray-500">
                                                                      • {getSubjectDisplayName(sel.subjectId)} (Group {sgIdx + 1}): {sg.facultyName || 'Not Assigned'}
                                                                    </div>
                                                                  );
                                                                })
                                                              ))}
                                                            </div>
                                                          );
                                                        }
                                                        return 'Multiple Faculty';
                                                      })()
                                                    : (entry.facultyName || 'No Faculty Assigned')}
                                            </div>
                                            {entrySessionType === 'lab' && entry.batchId && (
                                              <div className="text-purple-700 font-bold text-[10px] mt-1">
                                                {getBatchDisplayName(entry.batchId)}
                                              </div>
                                            )}
                                            </>
                                        )}
                                     </div>
                                   );
                                })}
                             </div>
                            </td>
                        );
                      } else {
                        return <td key={sIndex} className="border-r border-t border-gray-300 p-0 last:border-r-0"></td>;
                      }
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Signatory Modal */}
      {showSignatoryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-[500px] transform transition-all">
            <h3 className="text-xl font-bold text-gray-900 mb-2">Finalize & Download</h3>
            <p className="text-sm text-gray-500 mb-4">Add authorized signatories to appear at the bottom of the timetable.</p>
            
            <div className="mb-4">
                <div className="flex gap-2 mb-2">
                    {['Principal', 'HOD', 'Dean', 'Class Teacher'].map(role => (
                        <button key={role} onClick={() => handleAddSignatory(role)} className="px-3 py-1 bg-blue-50 text-blue-700 text-xs rounded-full border border-blue-100 hover:bg-blue-100 transition-colors">
                            + {role}
                        </button>
                    ))}
                </div>
                <div className="flex gap-2">
                    <input type="text" className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" 
                        placeholder="Custom Title (e.g. Director)" 
                        value={newSignatory} onChange={(e) => setNewSignatory(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleAddSignatory()}
                    />
                    <button onClick={() => handleAddSignatory()} className="px-4 py-2 bg-gray-800 text-white rounded-lg text-sm hover:bg-gray-900">Add</button>
                </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-6 min-h-[50px] bg-gray-50 p-3 rounded-lg border border-gray-100">
               {signatories.length === 0 && <span className="text-sm text-gray-400 italic self-center w-full text-center">No signatories added. PDF will be generated without signature blocks.</span>}
               {signatories.map((sig, i) => (
                  <span key={i} className="bg-white border px-3 py-1.5 rounded-md text-sm font-medium text-gray-700 flex items-center gap-2 shadow-sm">
                     {sig} <button onClick={() => handleRemoveSignatory(i)} className="text-red-400 hover:text-red-600 font-bold ml-1">×</button>
                  </span>
               ))}
            </div>

            <div className="flex justify-between items-center pt-2 border-t border-gray-100">
              <button onClick={() => setShowSignatoryModal(false)} className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm font-medium">Cancel</button>
              <button onClick={() => { setShowSignatoryModal(false); generatePDF(signatories); }} className="px-6 py-2.5 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 shadow-lg hover:shadow-xl transition-all">
                  Download Timetable
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="bg-white rounded-lg shadow-xl p-6 w-96">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Timetable?</h3>
            <p className="text-gray-600 text-sm mb-6">This action cannot be undone.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowDeleteModal(false)} className="px-4 py-2 bg-gray-100 rounded text-sm">Cancel</button>
              <button onClick={handleDeleteTimetable} className="px-4 py-2 bg-red-600 text-white rounded text-sm">Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {selectedSession && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="bg-white rounded-lg shadow-2xl p-6 w-[500px] max-h-[90vh] overflow-y-auto border-t-4 border-blue-600">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-bold text-gray-800">
                {selectedSession.sessionType === 'break' ? 'Break Session' : 
                 selectedSession.sessionType === 'custom' ? 'Custom Session' : 
                 selectedSession.sessionType === 'lab' ? 'Lab Session' : 
                 selectedSession.sessionType === 'language' ? 'Language Session' : 
                 selectedSession.sessionType === 'elective' ? 'Elective Session' : 'Session Details'}
              </h3>
              <button onClick={() => setSelectedSession(null)} className="text-gray-400 hover:text-red-500 font-bold text-xl">✕</button>
            </div>
            
            <div className="space-y-4">
               <div className="bg-gray-50 p-3 rounded text-sm border border-gray-200 grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-[10px] text-gray-500 font-bold uppercase block">Time</span> 
                    <span className="font-semibold text-gray-800">{formatTime12(selectedSession.timeSlot.start)} - {formatTime12(selectedSession.timeSlot.end)}</span>
                  </div>
                  <div>
                    <span className="text-[10px] text-gray-500 font-bold uppercase block">Room</span> 
                    <span className="font-semibold text-gray-800">{selectedSession.room || '—'}</span>
                  </div>
               </div>

               {selectedSession.sessionType === 'lab' && (
                 <div className="p-4 border rounded bg-purple-50 border-purple-200 text-center">
                   <div className="text-[10px] text-purple-600 font-bold uppercase mb-1">Lab Session</div>
                   <div className="font-bold text-gray-800 text-lg">{selectedSession.subjectName || getSubjectDisplayName(selectedSession.subjectId)}</div>
                   {selectedSession.subjectCode && <div className="text-sm text-gray-500 mt-1">{selectedSession.subjectCode}</div>}
                   <div className="text-sm text-gray-600 mt-2">
                     Batch: {selectedSession.batchName || getBatchDisplayName(selectedSession.batchId) || 'Not Specified'}
                   </div>
                   <div className="text-sm text-gray-600 mt-2 font-medium">
                     👨‍🏫 {selectedSession.facultyName || resolveFacultyLabel(selectedSession.facultyUserId) || 'No Faculty'}
                   </div>
                 </div>
               )}

               {(selectedSession.sessionType === 'break' || selectedSession.sessionType === 'custom') && (
                 <div className="p-4 border rounded bg-amber-50 border-amber-200 text-center">
                   <div className="text-[10px] text-amber-600 font-bold uppercase mb-1">Session Type</div>
                   <div className="font-bold text-gray-800 text-lg">{selectedSession.label || (selectedSession.sessionType === 'break' ? 'BREAK' : 'CUSTOM SESSION')}</div>
                 </div>
               )}

               {(selectedSession.sessionType === 'language' || selectedSession.sessionType === 'elective') && (
                 <div>
                   <h4 className="font-bold text-sm text-gray-700 mb-2 border-b pb-1 flex items-center gap-2">
                     <span>{selectedSession.sessionType === 'language' ? 'Language Allocations' : 'Elective Allocations'}</span>
                     {selectedSession.electiveGroupLabel && (
                       <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-semibold">
                         Label: {selectedSession.electiveGroupLabel}
                       </span>
                     )}
                   </h4>
                   <div className="mb-2 text-xs text-blue-700 font-semibold">
                     Group: {selectedSession.electiveGroupDisplay || getGroupDisplayName(selectedSession.electiveGroupId)}
                   </div>
                   <div className="max-h-60 overflow-y-auto space-y-3 pr-1">
                     {(selectedSession.electiveSelections || []).map((sel, i) => (
                       <div key={i} className="p-3 border rounded bg-white text-sm shadow-sm">
                         <div className="font-bold text-purple-800 flex items-center justify-between">
                           <span>{sel.subjectName || getSubjectDisplayName(sel.subjectId)}</span>
                           {sel.subjectCode && (
                             <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">{sel.subjectCode}</span>
                           )}
                         </div>
                         
                         {sel.subgroupSelections?.length > 0 ? (
                           <div className="mt-2 space-y-2">
                             {sel.subgroupSelections.map((sg, sgIdx) => (
                               <div key={sg.subGroupId} className="pl-2 border-l-2 border-purple-200 ml-1">
                                 <div className="text-xs text-gray-500">Group {sgIdx + 1}</div>
                                 <div className="text-sm font-medium text-gray-700">
                                   👨‍🏫 {sg.facultyName || resolveFacultyLabel(sg.facultyId) || 'Not Assigned'}
                                 </div>
                                 {sg.room && <div className="text-xs text-gray-500">Room: {sg.room}</div>}
                               </div>
                             ))}
                           </div>
                         ) : (
                           <div className="mt-2 flex items-center gap-2">
                             <span className="text-sm text-gray-600">👨‍🏫</span>
                             <span className="text-sm font-medium text-gray-700">
                               {sel.facultyName || resolveFacultyLabel(sel.facultyId) || 'Not Assigned'}
                             </span>
                           </div>
                         )}
                       </div>
                     ))}
                   </div>
                 </div>
               )}

               {selectedSession.sessionType === 'regular' && (
                 <div className="p-4 border rounded bg-blue-50 border-blue-200 text-center">
                   <div className="text-[10px] text-blue-600 font-bold uppercase mb-1">Subject</div>
                   <div className="font-bold text-gray-800 text-lg">{selectedSession.subjectName || getSubjectDisplayName(selectedSession.subjectId)}</div>
                   {(selectedSession.subjectCode || getSubjectCodeUpper(selectedSession.subjectId)) && (
                     <div className="text-xs text-gray-500 mt-1">{selectedSession.subjectCode || getSubjectCodeUpper(selectedSession.subjectId)}</div>
                   )}
                   <div className="text-sm text-gray-600 mt-3 font-medium flex items-center justify-center gap-2">
                     <span>👨‍🏫</span>
                     <span>{selectedSession.facultyName || resolveFacultyLabel(selectedSession.facultyUserId) || 'No Faculty'}</span>
                   </div>
                 </div>
               )}
            </div>
            
            <div className="mt-6 flex justify-end">
               <button onClick={() => setSelectedSession(null)} className="px-4 py-2 bg-gray-800 text-white rounded text-sm hover:bg-gray-900">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MasterTimetableView;